<script lang="ts">
	import Layout from "@components/layout/Layout.svelte";
	import image1 from "../../../core/assets/icons/EU_3.png?webp&w=150&imagetools";
	import { getEuProjektDetailPath } from "@core/schema/paths/getEuProjektDetailPath.js";
	import image2 from "../assets/eu/eu2.svg";
	import Formatted from "../components/common/Formatted.svelte";
	import Link from "../components/common/Link.svelte";
	import Media from "../components/common/Media.svelte";
	import TextWithIcon from "../components/common/TextWithIcon.svelte";
	import { ButtonVariant } from "../components/common/getButtonClass.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import { getTranslate } from "../utils/getTranslate.js";
	import translations from "./EuProjekt.translations.json" with { type: "json" };
	import type { EuProjektProps } from "./EuProjektProps.js";

	const _: EuProjektProps = $props();
	const translate = getTranslate(translations);
	const { tenant } = getGlobalTemplateProps();

	// Generate localized paths for detail pages
	const project1Path = getEuProjektDetailPath(
		tenant,
		{
			cs: "dvoustupnova-zplynovaci-technologie",
			en: "two-stage-gasification-technology",
			sk: "dvoustupnova-splynovacia-technologia",
		}[tenant.locale],
	);
	const project2Path = getEuProjektDetailPath(
		tenant,
		{
			cs: "prihlaska-vynalezu-a-registrace-vzoru",
			en: "patent-application-and-design-registration",
			sk: "prihlaska-vynalezu-a-registracia-vzorov",
		}[tenant.locale],
	);
	const project3Path = getEuProjektDetailPath(
		tenant,
		{
			cs: "redox-green-innovation",
			en: "redox-green-innovation",
			sk: "redox-green-innovation",
		}[tenant.locale],
	);
</script>

<Layout>
	<section class="container-center pt-8 md:pt-12 lg:pt-16 pb-8 md:pb-12">
		<h1 class="text-zms-blue sm:text-2xl md:text-3xl lg:text-5xl font-semibold mb-4">
			<Formatted text={translate("pageTitle")} />
		</h1>
		<p class="text-optical-black text-base md:text-lg max-w-3xl">
			<Formatted text={translate("pageDescription")} />
		</p>
	</section>

	<section class="container-center pb-8 md:pb-12 lg:pb-16">
		<div class="flex flex-col gap-6">
			<div
				class="bg-zms-grey border border-zms-card-border rounded-2xl duration-300 transition-all hover:shadow-lg hover:shadow-black/10 hover:-translate-y-1 overflow-hidden"
			>
				<div class="flex flex-col lg:flex-row gap-6 lg:gap-8 p-6 lg:p-8">
					<div class="flex-grow flex flex-col justify-between gap-5">
						<div class="flex flex-col gap-3">
							<h3 class="font-semibold text-lg md:text-2xl font-hubot tracking-[-1%] text-zms-blue-dark leading-tight">
								<Formatted text={translate("project1Title")} />
							</h3>
							<p class="font-normal text-sm md:text-base tracking-[-1%] text-zms-optical-black leading-relaxed">
								<Formatted text={translate("project1ShortDesc")} />
							</p>
						</div>

						<div class="flex items-center">
							<Link href={project1Path} variant={ButtonVariant.TransparentWithIcon}>
								<TextWithIcon transparent>{translate("viewDetails")}</TextWithIcon>
							</Link>
						</div>
					</div>
					<div class="flex-shrink-0 flex flex-col items-center justify-center gap-3">
						<Media
							class="w-full h-auto max-w-[120px] lg:max-w-[150px] object-contain"
							source={{ src: image1, type: "image/webp" }}
							width={150}
							height={0}
							alt={translate("fundedByEu")}
						/>
						<img src={image2} alt="EU" class="w-full h-auto max-w-[120px] lg:max-w-[150px] object-contain" />
					</div>
				</div>
			</div>

			<div
				class="bg-zms-grey border border-zms-card-border rounded-2xl duration-300 transition-all hover:shadow-lg hover:shadow-black/10 hover:-translate-y-1 overflow-hidden"
			>
				<div class="flex flex-col lg:flex-row gap-6 lg:gap-8 p-6 lg:p-8">
					<div class="flex-grow flex flex-col justify-between gap-5">
						<div class="flex flex-col gap-3">
							<h3 class="font-semibold text-lg md:text-2xl font-hubot tracking-[-1%] text-zms-blue-dark leading-tight">
								<Formatted text={translate("project2Title")} />
							</h3>
							<p class="font-normal text-sm md:text-base tracking-[-1%] text-zms-optical-black leading-relaxed">
								<Formatted text={translate("project2ShortDesc")} />
							</p>
						</div>

						<div class="flex items-center">
							<Link href={project2Path} variant={ButtonVariant.TransparentWithIcon}>
								<TextWithIcon transparent>{translate("viewDetails")}</TextWithIcon>
							</Link>
						</div>
					</div>
					<div class="flex-shrink-0 flex flex-col items-center justify-center gap-3">
						<Media
							class="w-full h-auto max-w-[120px] lg:max-w-[150px] object-contain"
							source={{ src: image1, type: "image/webp" }}
							width={150}
							height={0}
							alt={translate("fundedByEu")}
						/>
						<img src={image2} alt="EU" class="w-full h-auto max-w-[120px] lg:max-w-[150px] object-contain" />
					</div>
				</div>
			</div>

			<div
				class="bg-zms-grey border border-zms-card-border rounded-2xl duration-300 transition-all hover:shadow-lg hover:shadow-black/10 hover:-translate-y-1 overflow-hidden"
			>
				<div class="flex flex-col lg:flex-row gap-6 lg:gap-8 p-6 lg:p-8">
					<div class="flex-grow flex flex-col justify-between gap-5">
						<div class="flex flex-col gap-3">
							<h3 class="font-semibold text-lg md:text-2xl font-hubot tracking-[-1%] text-zms-blue-dark leading-tight">
								<Formatted text={translate("project3Title")} />
							</h3>
							<p class="font-normal text-sm md:text-base tracking-[-1%] text-zms-optical-black leading-relaxed">
								<Formatted text={translate("project3ShortDesc")} />
							</p>
						</div>

						<div class="flex items-center">
							<Link href={project3Path} variant={ButtonVariant.TransparentWithIcon}>
								<TextWithIcon transparent>{translate("viewDetails")}</TextWithIcon>
							</Link>
						</div>
					</div>
				</div>
			</div>
		</div>
	</section>

	<section class="full-width bg-zms-grey grid-container">
		<div class="container-center py-8 md:py-12">
			<div class="text-center mb-8">
				<p class="text-optical-black text-base md:text-lg lg:text-xl font-semibold">
					{translate("fundedByEu")}
				</p>
			</div>
		</div>
	</section>
</Layout>
